<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-4 align-center">
        <h2>{{ buyingStationName }}</h2>
        <v-spacer></v-spacer>
        <div class="d-flex align-center">
          <v-btn color="primary" dark class="mr-2 mb-2" @click="handleAddFarm" v-if="getModulePermittedActions.post">
            {{ $t("memberDataSection.associateFarm") }}
          </v-btn>
          <v-btn color="primary" dark class="mr-2 mb-2" @click="handleBack">
            {{ $t("memberDataSection.back") }}
          </v-btn>
        </div>
      </div>

      <v-card elevation="0" class="px-5">
        <v-card-title class="text-h6" style="margin-left:-20px;">
          {{ $t("memberDataSection.associatedFarms") }}
        </v-card-title>
        <v-card-text>
          <v-row style="margin-left:-35px; margin-bottom: 20px;">
            <v-col cols="12">
                <v-data-table 
                  :headers="tableHeaders" 
                  :items="associatedFarms" 
                  :items-per-page="10" 
                  :footer-props="{
                    'items-per-page-options': [5, 10, 25, 50],
                  }" 
                  item-key="id" 
                  hide-default-footer 
                  :loading="loading" 
                  :options="options"
                  @update:options="handleTableUpdate"
                  :server-items-length="totalAssociatedFarms" 
                  :loading-text="$t('loadingData')" 
                  class="elevation-1"
                  single-expand
                  show-expand
                >
                <template v-slot:top="{}">
                  <div class="d-flex align-center">

                    <div class="ml-3 mt-6" style="width: 220px;">
                      <v-text-field style="width: 280px;" prepend-inner-icon="mdi-magnify" outlined height="5px"
                        :placeholder="$t('memberDataSection.search')" dense v-model="search" @input="searchQuery"
                        class="shrink">
                      </v-text-field>
                    </div>

                    <div class="ml-3" style="width: 250px;">
                      <label for="text">{{ $t("memberDataSection.eudrDeforestationStatus") }}</label>
                      <v-tooltip top color="00BD73" max-width="350">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon class="icon-with-background primary--text" style="color:#0EBF67" v-bind="attrs" v-on="on">
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <span>{{ $t("memberDataSection.eudrDeforestationStatusTT") }}</span>
                      </v-tooltip>
                      <v-autocomplete clearable style="width: 250px;" v-model="deforestationStatusModel" item-text="text" item-value="val"
                        :items="deforestationStatuses" :placeholder="$t('select')" @change="filterData"
                        outlined dense>
                      </v-autocomplete>
                    </div>

                    <v-spacer></v-spacer>
                    <div class="d-flex align-center mb-3">
                      <div class="py-1 title">
                        {{ fromAssociatedFarmsList }} -
                        {{ toAssociatedFarmsList }} {{ $t('of') }}
                        {{ totalAssociatedFarms }}
                      </div>
                      <v-btn class="mx-2" fab small outlined color="primary" :disabled="options.page <= 1"
                        @click="reportPageChange(false)">
                        <v-icon dark>
                          mdi-chevron-left
                        </v-icon>
                      </v-btn>
                      <v-btn class="mx-2" fab small outlined color="primary" :disabled="options.page >= totalPages"
                        @click="reportPageChange(true)">
                        <v-icon dark>
                          mdi-chevron-right
                        </v-icon>
                      </v-btn>
                      <v-menu v-model="menu" :close-on-content-click="false" :nudge-width="200" left :min-width="417">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                            <v-icon dark>mdi-cog-outline </v-icon>
                          </v-btn>
                        </template>
                        <v-card width="417">
                          <v-list>
                            <v-list-item>
                              <v-list-item-content>
                                <v-list-item-title>{{ $t("deforestation.customization") }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                            <v-divider></v-divider>
                            <div class="pl-3 pt-2 font-weight-normal">
                              {{ $t("deforestation.recordsPerpage") }}
                            </div>
                            <v-list-item>
                              <v-list-item-content>
                                <v-slider v-model="itemsPerPageSlider" :tick-labels="itemsPerPageOptions" :max="3"
                                  step="1" ticks="always" tick-size="4"></v-slider>
                              </v-list-item-content>
                            </v-list-item>
                            <v-divider class="mt-n3"></v-divider>
                          </v-list>
                          <v-card-text>
                            <v-container fluid>
                              <v-row>
                                <v-col class="mt-n9" cols="6" v-for="header in headers" :key="header.value">
                                  <v-checkbox v-if="header.text" v-model="selectedHeaders" :label="header.text"
                                    color="primary" :value="header.text" hide-details
                                    :disabled="header.text == 'ID'"></v-checkbox>
                                </v-col>
                              </v-row>
                            </v-container>
                          </v-card-text>
                          <v-layout justify-center>
                            <v-card-actions class="mb-5">
                              <v-spacer></v-spacer>
                              <v-btn outlined color="primary" @click="resetDefault" width="190">
                                <span class="text-truncate" style="max-width:150px">{{ $t("deforestation.restoreDefault") }}</span>
                              </v-btn>
                              <v-btn color="primary" @click="resetTableStructure" width="190">
                                {{ $t("deforestation.apply") }}
                              </v-btn>
                            </v-card-actions>
                          </v-layout>
                        </v-card>
                      </v-menu>

                      <v-tooltip top color="black" max-width="350">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on"
                            @click="exportToPDF">
                            <v-icon dark> mdi-download </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("deforestation.download") }}</span>
                      </v-tooltip>
                      <v-tooltip top color="black" max-width="350">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on"
                            @click="printPDF('document')">
                            <v-icon dark> mdi-printer </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("deforestation.print") }}</span>
                      </v-tooltip>
                    </div>
                  </div>
                </template>
                <template v-slot:item.farmNameAndId="{ item }">
                  <div
                    class="text-decoration-underline cursor-pointer primary--text font-weight-medium"
                    @click="viewFarm(item.id, 0)"
                  >
                    {{ item.farmName || "N/A" }}
                  </div>
                  <div class="text-caption grey--text text--darken-1 mt-1">
                    {{ item.registrationNo || item.id || "N/A" }}
                  </div>
                </template>

                <template v-slot:item.farmerName="{ item }">
                  <div
                    v-if="item.farmerUserId && item.ownerName"
                    class="text-decoration-underline cursor-pointer primary--text"
                    @click="$router.push({ name: 'farmers_view', params: { id: item.farmerUserId } })"
                  >
                    {{ item.ownerName }}
                  </div>
                  <div v-else>{{ item.ownerName || "N/A" }}</div>
                </template>

                <template v-slot:item.addressOfProduction="{ item }">
                  {{ item.addressLine1 || "N/A" }}<br />
                  {{ item.addressLine2 }}
                </template>

                <!-- Registered By & Date: two-line cell, matches Farm Management list -->
                <template v-slot:item.regByDate="{ item }">
                  <v-tooltip bottom color="black" max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        v-bind="attrs"
                        v-on="on"
                        class="text-decoration-underline cursor-pointer primary--text font-weight-medium"
                      >
                        {{ item.registeredBy || "N/A" }}
                      </div>
                    </template>
                    <div>
                      <div>
                        {{ $t("memberDataSection.userId") }}:
                        <b>{{ item.registeredById || "N/A" }}</b>
                      </div>
                      <div>
                        {{ $t("memberDataSection.userName") }}:
                        <b>{{ item.registeredBy || "N/A" }}</b>
                      </div>
                      <div>
                        {{ $t("memberDataSection.userRole") }}:
                        <b>{{ item.registeredByRole || "N/A" }}</b>
                      </div>
                    </div>
                  </v-tooltip>
                  <div class="text-caption grey--text text--darken-1 mt-1">
                    {{ formatDate(item.createdAt) }}
                  </div>
                </template>


                <template v-slot:item.area="{ item }">
                  {{ item.area }}
                </template>
                
                <template v-slot:item.type="{ item }">
                  {{ item.type || "N/A" }}
                </template>
                
                <template v-slot:item.status="{ item }">
                  <v-btn small rounded min-width="100" depressed class="green-button" height="32"
                      v-if="item.status === 'compliant'">
                      {{ $t("memberDataSection.compliant") }}
                  </v-btn>
                  <v-btn small rounded min-width="100" depressed class="red-button" height="32"
                      v-else-if="item.status === 'non-compliant'">
                      {{ $t("memberDataSection.nonCompliant") }}
                  </v-btn>
                  <v-btn small rounded min-width="100" depressed class="gray-button" height="32"
                      v-else>
                      {{ $t("memberDataSection.pending") }}
                  </v-btn>
                </template>
                
                <template v-slot:item.actions="{ item }">
                  <v-menu location="start">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click.stop small>
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="editFarm(item.id)" :disabled="!getModulePermittedActions.put">
                        <v-list-item-title>{{ $t('memberDataSection.edit') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="deleteFarm(item.id)" :disabled="!getModulePermittedActions.delete">
                        <v-list-item-title class="red--text">{{ $t('memberDataSection.delete') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
                
                <template v-slot:item.data-table-expand="{ item, expand, isExpanded }">
                  <v-btn icon @click="expand(!isExpanded)" small>
                    <v-icon>{{ isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                  </v-btn>
                </template>

                <template v-slot:expanded-item="{ item, headers }">
                  <td :colspan="headers.length" class="pa-0">
                    <div class="expanded-content">
                      <div class="horizontal-line"></div>
                      
                      <div class="two-column-layout">
                        <div class="column-left">
                          <div class="section-header">
                            <h4 class="section-title">{{ $t("memberDataSection.eudrDeforestationStatus") }}</h4>
                          </div>
                          <div class="section-body">
                            <div class="button-with-icon">
                              <v-btn 
                                rounded 
                                depressed 
                                :class="getEudrStatusButtonClass(item.eudrStatus)" 
                                min-width="200" 
                                height="32"
                              >
                                {{ getEudrStatusText(item.eudrStatus) }}
                              </v-btn>
                              <v-tooltip top color="00BD73" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <img v-bind="attrs" v-on="on" class="ml-2" src="/icons/todo-list.png"
                                    style="width: 34px; height: 34px; border-radius: 50%; cursor: pointer;" 
                                    @click="viewEudrReport(item)" />
                                </template>
                                <span>{{ $t('dueDiligence.viewReport') }}</span>
                              </v-tooltip>
                            </div>
                            <div v-if="item.deforestationData" class="assessment-details">
                              <div class="detail-item">
                                <span class="detail-label">{{ $t("memberDataSection.overallProbability") }}:</span>
                                <span class="detail-value">{{ formatProbability(item.deforestationData.overallProb) }}%</span>
                              </div>
                              <div v-if="item.deforestationData.deforestationArea" class="detail-item">
                                <span class="detail-label">{{ $t("memberDataSection.deforestationArea") }}:</span>
                                <span class="detail-value">{{ item.deforestationData.deforestationArea }} ha</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div class="vertical-line"></div>
                        
                        <div class="column-right">
                          <div class="section-header">
                            <h4 class="section-title">{{ $t("memberDataSection.assessments") }}</h4>
                          </div>
                          <div class="section-body">
                            <div class="assessment-item">
                              <div class="assessment-label font-weight-bold">
                                {{ $t("memberDataSection.riskAssessment") }}
                              </div>
                              <div class="assessment-value">
                                <div class="button-with-icon">
                                  <v-btn 
                                    rounded 
                                    depressed 
                                    :class="getRiskAssessmentButtonClass(item.riskAssessmentStatus)" 
                                    min-width="150" 
                                    height="32"
                                  >
                                    {{ getRiskAssessmentText(item.riskAssessmentStatus) }}
                                  </v-btn>
                                  <v-tooltip top color="00BD73" max-width="350">
                                    <template v-slot:activator="{ on, attrs }">
                                      <img v-bind="attrs" v-on="on" class="ml-2" src="/icons/todo-list.png"
                                        style="width: 34px; height: 34px; border-radius: 50%; cursor: pointer;" 
                                        @click="viewRiskAssessmentReport(item)" />
                                    </template>
                                    <span>{{ $t('dueDiligence.viewReport') }}</span>
                                  </v-tooltip>
                                </div>
                              </div>
                            </div>
                            <div class="assessment-item">
                              <div class="assessment-label">
                                {{ $t("memberDataSection.dateOfSubmission") }}
                              </div>
                              <div class="assessment-value">
                                {{ item.deforestationData ? formatDate(item.deforestationData.createdAt) : 'N/A' }}
                              </div>
                            </div>
                            <div class="assessment-item">
                              <div class="assessment-label">
                                {{ $t("memberDataSection.dateOfExpiry") }}
                              </div>
                              <div class="assessment-value">
                                {{ item.deforestationData ? formatDate(item.deforestationData.updatedAt) : 'N/A' }}
                              </div>
                            </div>
                            <div v-if="item.deforestationData && item.deforestationData.metrics" class="assessment-item">
                              <div class="assessment-label">
                                {{ $t("memberDataSection.assessmentMetrics") }}
                              </div>
                              <div class="assessment-value">
                                <div v-for="metric in item.deforestationData.metrics.slice(0, 3)" :key="metric.label" class="metric-item">
                                  <span class="metric-label">{{ metric.label }}:</span>
                                  <span class="metric-value">{{ formatProbability(metric.percent) }}%</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </template>
                
                <template v-slot:footer></template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

    </v-container>

    <confirm-box
      :title="confirmElements.title"
      :message="confirmElements.message"
      :cancelBtnText="confirmElements.cancelBtnText"
      :confirmBtnText="confirmElements.confirmBtnText"
      :confirmData="confirmElements.confirmData"
      :dialog="confirm"
      @cancel:action="confirmCancel"
      @continue:action="confirmDelete"
    />

    <v-dialog v-model="addFarmDialog" max-width="860" persistent>
      <v-card>
        <v-card-title class="pb-0">
          <span class="text-h6">{{ $t('memberDataSection.addFarm') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeAddFarmDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-stepper v-model="addFarmStep" flat class="elevation-0">
          <v-stepper-header style="box-shadow:none; border-bottom:1px solid #e0e0e0;">
            <v-stepper-step :complete="addFarmStep > 1" step="1">{{ $t('memberDataSection.selectFarms') }}</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="2">{{ $t('memberDataSection.reviewSelection') }}</v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content step="1" class="pa-4">
              <v-text-field
                v-model="unmappedSearch"
                prepend-inner-icon="mdi-magnify"
                :placeholder="$t('memberDataSection.search')"
                outlined
                dense
                clearable
                @input="unmappedSearchQuery"
                class="mb-3"
                hide-details
              ></v-text-field>

              <v-data-table
                v-model="selectedFarms"
                :headers="unmappedTableHeaders"
                :items="unmappedFarms"
                :loading="unmappedFarmsLoading"
                :server-items-length="unmappedFarmsTotal"
                :items-per-page="unmappedOptions.limit"
                hide-default-footer
                show-select
                item-key="id"
                class="elevation-1"
              >
                <template v-slot:footer>
                  <div class="d-flex align-center justify-end pa-2">
                    <span class="caption mr-3">
                      {{ (unmappedOptions.page - 1) * unmappedOptions.limit + 1 }}–{{ Math.min(unmappedOptions.page * unmappedOptions.limit, unmappedFarmsTotal) }}
                      {{ $t('of') }} {{ unmappedFarmsTotal }}
                    </span>
                    <v-btn icon small :disabled="unmappedOptions.page <= 1" @click="unmappedOptions.page--; getUnmappedFarms()">
                      <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <v-btn icon small :disabled="unmappedOptions.page * unmappedOptions.limit >= unmappedFarmsTotal" @click="unmappedOptions.page++; getUnmappedFarms()">
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-stepper-content>

            <v-stepper-content step="2" class="pa-4">
              <div class="mb-3 body-2">{{ $t('memberDataSection.reviewMappingPrompt', { count: selectedFarms.length }) }}</div>
              <v-data-table
                :headers="unmappedTableHeaders"
                :items="selectedFarms"
                hide-default-footer
                class="elevation-1"
              ></v-data-table>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <template v-if="addFarmStep === 1">
            <v-btn text @click="closeAddFarmDialog">{{ $t('cancel') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" :disabled="selectedFarms.length === 0" @click="addFarmStep = 2">
              {{ $t('memberDataSection.next') }} ({{ selectedFarms.length }})
            </v-btn>
          </template>
          <template v-else>
            <v-btn text @click="addFarmStep = 1">{{ $t('memberDataSection.back') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" :loading="mappingLoading" @click="confirmMapFarms">
              {{ $t('memberDataSection.confirmMapping') }}
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import download from "@/mixins/DownloadMixin";
import getPermittedActions from "@/mixins/GetPermittedActions";
import DateMixin from "@/mixins/DateMixin";
import html2pdf from "html2pdf.js";
import printJS from "print-js";
import moment from 'moment';
import RoleMixin from "@/mixins/RoleMixin";
import { isKenyaClient, isIndonesianClient, decodeHtmlEntities } from "@/utils";
import BuyingStationService from "@/_services/BuyingStationService";
import FarmService from "@/_services/FarmService";
import DeforestationService from "@/_services/DeforestationService";
import ConfirmBox from "@/components/ConfirmBox";
import axios from 'axios';

export default {
  created() {
    this.buyingStationName = this.$route.params.buyingStationName || (this.isNaccuUser ? 'Factory' : 'Buying Station');
    // The initial fetch will be triggered by @update:options from the v-data-table when it mounts.
  },
  async mounted() {
    this.tableHeaders = this.headers;
    this.selectedHeaders = this.allSelectedHeaders;
  },
  computed: {
    isNaccuUser() {
      return isKenyaClient();
    },
    isIndonesianUser() {
      return isIndonesianClient();
    },
    isOwner() {
      return this.getCurrentRoles.some(x => ["operator_owner", "supplier_owner"].includes(x))
    },
    getOrganizationId() {
      return this.$store.getters.getUser?.user_organization?.id;
    },
    reportHeaders() {
      return this.headers.filter(header => this.selectedHeaders.includes(header.text));
    },
    totalPages() {
      return parseInt(Math.ceil(this.totalAssociatedFarms / this.options.limit))
    },
    unmappedTableHeaders() {
      return [
        { text: this.$t('memberDataSection.farmNameAndId'), value: 'farmName', sortable: false },
        { text: 'Reg. No', value: 'registrationNo', sortable: false },
        { text: this.$t('memberDataSection.owner'), value: 'ownerName', sortable: false },
        { text: this.$t('memberDataSection.addressOfProduction'), value: 'address', sortable: false },
        { text: this.$t('memberDataSection.areaHa'), value: 'areaHa', sortable: false },
      ];
    },
    breadcrumbs() {
      return [
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: "/",
        },
        {
          text: this.isNaccuUser ? this.$t("memberDataSection.factories") : this.$t("memberDataSection.buyingStation"),
          disabled: false,
          to: "/coffee/buying-station",
        },
        {
          text: this.$t("memberDataSection.associatedFarms"),
          disabled: true,
          href: "breadcrumbs_link_2",
        },
      ];
    },
  },
  data() {
    return {
      debounceTimer: null,
      fromAssociatedFarmsList: 0,
      toAssociatedFarmsList: 0,
      search: "",
      deforestationStatusModel: "",
      copyReport: [],
      loading: true,
      selectedItem: false,
      expanded: [],
      buyingStationName: '',
      initialLoadComplete: false,
      
      activeController: null,
      lastFetchParams: '',

      options: {
        limit: 10,
        page: 1,
      },
      headers: [
        {
          text: this.$t("memberDataSection.farmNameAndId"),
          align: "start",
          value: "farmNameAndId",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("farm.producerFarmer"),
          align: "start",
          value: "farmerName",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("memberDataSection.addressOfProduction"),
          align: "start",
          value: "addressOfProduction",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("farm.regByDate"),
          align: "start",
          value: "regByDate",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("memberDataSection.areaHa"),
          align: "start",
          value: "area",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("memberDataSection.type"),
          align: "start",
          value: "type",
          class: "black--text",
          sortable: true,
        },
        {
          text: this.$t("memberDataSection.status"),
          align: "start",
          value: "status",
          class: "black--text",
          sortable: true,
        },
        {
          text: "",
          value: "actions",
          sortable: false,
          width: "60px",
        },
        {
          text: "",
          value: "data-table-expand",
          sortable: false,
          width: "40px",
        },
      ],
      allSelectedHeaders: [
        this.$t('memberDataSection.farmNameAndId'),
        this.$t('farm.producerFarmer'),
        this.$t('memberDataSection.addressOfProduction'),
        this.$t('farm.regByDate'),
        this.$t('memberDataSection.areaHa'),
        this.$t("memberDataSection.type"),
        this.$t("memberDataSection.status"),
        "",
        "",
      ],
      menu: false,
      associatedFarms: [],
      totalAssociatedFarms: 0,
      totalAssociatedFarmPages: 0,
      customizeMenu: false,
      customizeMenuAll: false,
      itemsPerPageOptions: [5, 10, 25, 50],
      itemsPerPageSlider: 1,
      selectedHeaders: [],
      tableHeaders: [],
      deforestationStatuses: [
        { text: this.$t("deforestation.allStatus"), val: ""},
        { text: this.$t("deforestation.veryHighProbability"), val: "Very High Deforestation Probability"},
        { text: this.$t("deforestation.highProbability"), val: "High Deforestation Probability" },
        { text: this.$t("deforestation.mediumProbability"), val: "Medium Deforestation Probability" },
        { text: this.$t("deforestation.lowProbability"), val: "Low Deforestation Probability" },
        { text: this.$t("deforestation.veryLowProbability"), val: "Very Low Deforestation Probability" },
        { text: this.$t("deforestation.zeroOrNegligibleProbability"), val: "Zero/Negligible Deforestation Probability"},
        { text: this.$t("dueDiligence.manualMitigated"), val: "Manually Mitigated" },
      ],
      addFarmDialog: false,
      addFarmStep: 1,
      unmappedFarms: [],
      unmappedFarmsTotal: 0,
      unmappedFarmsLoading: false,
      unmappedSearch: '',
      unmappedDebounceTimer: null,
      unmappedOptions: { page: 1, limit: 10 },
      selectedFarms: [],
      mappingLoading: false,

      confirm: false,
      confirmElements: {
        title: this.$t("farm.deleteFarm"),
        message: this.$t("farm.confirmDeleteFarm"),
        confirmBtnText: this.$t("yes"),
        cancelBtnText: this.$t("cancel"),
        confirmData: {},
      },
    };
  },
  methods: {
    handleTableUpdate(newOptions) {
      this.options = { ...this.options, ...newOptions };
      this.getAssociatedFarms();
    },

    async exportToPDF() {
      try {
        this.startLoading();
        await this.printPDF();
        this.stopLoading();
      } catch (error) {
        console.error('Error exporting PDF:', error);
        this.stopLoading();
      }
    },

    async printPDF() {
      await this.$nextTick();
      const element = document.querySelector(".v-data-table__wrapper");
      let opt = {
        margin: 1,
        filename: "AssociatedFarmsList.pdf",
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
      };

      let worker = await html2pdf()
        .set(opt)
        .from(element)
        .toCanvas()
        .outputPdf("blob", "AssociatedFarms.pdf");
      let file = new File([worker], "AssociatedFarms.pdf", {
        type: "application/pdf",
      });
      var pdfUrl = await URL.createObjectURL(file);
      await printJS({ printable: `${pdfUrl}`, type: "pdf", showModal: true });
    },

    async resetDefault() {
      this.tableHeaders = this.headers;
      this.selectedHeaders = this.allSelectedHeaders;
      this.menu = false;
      this.options.page = 1;
      await this.getAssociatedFarms();
    },

    async resetTableStructure() {
      this.tableHeaders = this.headers.filter((header) =>
        this.selectedHeaders.includes(header.text)
      );
      this.options.limit = this.itemsPerPageOptions[
        this.itemsPerPageSlider
      ];
      this.options.page = 1;
      await this.getAssociatedFarms();
      this.customizeMenu = false; 
      this.customizeMenuAll = false;
    },

    getDateFormat(date) {
      return moment(date).format("DD/MM/YYYY");
    },

    handleAddFarm() {
      this.addFarmDialog = true;
      this.addFarmStep = 1;
      this.selectedFarms = [];
      this.unmappedSearch = '';
      this.unmappedOptions = { page: 1, limit: 10 };
      this.getUnmappedFarms();
    },

    closeAddFarmDialog() {
      this.addFarmDialog = false;
      this.selectedFarms = [];
      this.unmappedFarms = [];
      this.unmappedFarmsTotal = 0;
    },

    unmappedSearchQuery() {
      clearTimeout(this.unmappedDebounceTimer);
      this.unmappedDebounceTimer = setTimeout(() => {
        this.unmappedOptions.page = 1;
        this.getUnmappedFarms();
      }, 400);
    },

    async getUnmappedFarms() {
      this.unmappedFarmsLoading = true;
      try {
        const response = await BuyingStationService.getUnmappedFarms(this.$route.params.id, {
          page: this.unmappedOptions.page,
          limit: this.unmappedOptions.limit,
          search: this.unmappedSearch,
        });
        const raw = response?.data?.info || [];
        this.unmappedFarms = raw.map(f => ({
          ...f,
          areaHa: f.area ? ((f.area || 0) / 2.471).toFixed(2) : '—',
        }));
        this.unmappedFarmsTotal = response?.data?.total || 0;
      } catch (e) {
        console.error('Failed to load unmapped farms:', e);
      } finally {
        this.unmappedFarmsLoading = false;
      }
    },

    async confirmMapFarms() {
      this.mappingLoading = true;
      try {
        const farmIds = this.selectedFarms.map(f => f.id);
        await BuyingStationService.mapFarmsToStation(this.$route.params.id, farmIds);
        this.closeAddFarmDialog();
        this.lastFetchParams = '';
        await this.getAssociatedFarms();
        this.$notify({ title: this.$t('memberDataSection.farmsMappedSuccess'), type: 'success' });
      } catch (e) {
        this.$notify({ title: this.$t('memberDataSection.farmsMappedError'), type: 'error' });
      } finally {
        this.mappingLoading = false;
      }
    },

    handleBack() {
      this.$router.push({
        path: `/coffee/buying-station/${this.$route.params.id}`,
      });
    },

    viewFarm(farmId, tabId) {
      this.$router.push({ name: "FarmInfo", params: { farmId, tabId } });
    },

    editFarm(id) {
      this.$router.push({
        name: 'EditFarms',
        params: { farmId: id },
      });
    },

    deleteFarm(id) {
      this.confirmElements.title = this.$t("farm.deleteFarm");
      this.confirmElements.message = this.$t("farm.confirmDeleteFarm");
      this.confirmElements.confirmBtnText = this.$t("yes");
      this.confirmElements.confirmData = { farmId: id };
      this.confirm = true;
    },

    confirmCancel() {
      this.confirm = false;
    },

    async confirmDelete(action, confirmData) {
      if (action) {
        await this.startLoading();
        FarmService.deactiveFarm(confirmData.farmId)
          .then(async (res) => {
            if (res.success) {
              await this.stopLoading();
              await this.getAssociatedFarms();
              this.confirm = false;
              this.$notify({
                title: this.$t("farm.deleted"),
                type: "success",
              });
            } else {
              await this.stopLoading();
              this.confirm = false;
              this.$notify({
                title: this.$t("farm.notDeleted"),
                type: "error",
              });
            }
          })
          .catch(async () => {
            await this.stopLoading();
            this.confirm = false;
            this.$notify({
              title: this.$t("farm.notDeleted"),
              type: "error",
            });
          });
      } else {
        this.confirm = false;
      }
    },

    filterData() {
      this.options.page = 1;
      this.getAssociatedFarms();
    },

    searchQuery() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.options.page = 1;
        this.getAssociatedFarms();
      }, 500)
    },

    async getAssociatedFarms() {
      const stateFingerprint = JSON.stringify({
        page: this.options.page,
        limit: this.options.limit,
        search: this.search,
        status: this.deforestationStatusModel
      });

      if (this.lastFetchParams === stateFingerprint) return;
      this.lastFetchParams = stateFingerprint;

      if (this.activeController) {
        this.activeController.abort();
      }
      this.activeController = new AbortController();

      this.loading = true;
      
      try {
        const queryParams = {
          page: this.options.page,
          limit: this.options.limit,
          search: this.search || ""
        };

        const promises = [
          BuyingStationService.getFarmsByFactories(this.$route.params.id, queryParams, {
            signal: this.activeController.signal
          })
        ];
        
        if (!this.initialLoadComplete) {
          promises.push(BuyingStationService.getBuyingStationDetails(this.$route.params.id));
        }

        const [farmsResponse, stationResponse] = await Promise.all(promises);
        
        if (stationResponse && stationResponse.data) {
          this.buyingStationName = stationResponse.data.fullName || 
            `${stationResponse.data.firstName || ''} ${stationResponse.data.middleName || ''} ${stationResponse.data.lastName || ''}`.trim() ||
            (this.isNaccuUser ? 'Factory' : 'Buying Station');
        }
        
        const farmsData = farmsResponse?.data?.data || farmsResponse?.data?.info || [];
        const totalCount = farmsResponse?.data?.total || 0;
        
        let farms = farmsData.map(farm => ({
          id: farm.id,
          farmName: farm.farmName,
          addressLine1: decodeHtmlEntities(farm.address) || '',
          addressLine2: decodeHtmlEntities(farm.district || farm.city) || '',
          area: farm?.area ? ((farm.area || 0)/2.471).toFixed(5) : 0,
          type: this.getFarmType(farm),
          status: 'n/a', 
          createdAt: farm.createdAt,
          geofence: farm.geofence,
          lat: farm.lat,
          log: farm.log,
          ownerName: this.getFarmerName(farm),
          farmerUserId: farm.user?.id || null,
          ...this.getRegisteredBy(farm),
          registrationNo: farm.registrationNo,
          farmingActivity: farm.farmingActivity,
          productionSystem: farm.productionSystem,
          segments: farm.segments || [],
          coordinates: farm.coordinates || [],
          eudrStatus: null,
          riskAssessmentStatus: null,
          deforestationData: null
        }));
        
        await this.fetchEudrStatusForFarms(farms);
        
        if (this.deforestationStatusModel) {
          farms = farms.filter(farm => farm.status === this.deforestationStatusModel);
        }
        
        this.associatedFarms = farms;
        this.totalAssociatedFarms = totalCount;
        this.totalAssociatedFarmPages = Math.ceil(this.totalAssociatedFarms / this.options.limit);

        this.fromAssociatedFarmsList = this.totalAssociatedFarms > 0
          ? (this.options.page - 1) * this.options.limit + 1
          : 0;
        this.toAssociatedFarmsList = this.options.page * this.options.limit > this.totalAssociatedFarms
          ? this.totalAssociatedFarms
          : this.options.page * this.options.limit;

        this.loading = false;
        this.initialLoadComplete = true; 
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Previous request canceled');
        } else {
          console.error('Error fetching farms:', error);
          this.loading = false;
          this.initialLoadComplete = true; 
        }
      }
    },

    getFarmType(farm) {
      // Backend resolves this from user_farm.polygon_coordinates + the farm's geofences.
      if (farm?.geometryType) return farm.geometryType;

      // Fallback for payloads that predate geometryType.
      const coords = farm?.farmCoordinates || farm?.coordinates || [];
      return coords.length >= 3 ? "Polygon" : "N/A";
    },

    getFarmerName(farm) {
      const parts = (person) =>
        person
          ? [person.firstName, person.middleName, person.lastName]
              .filter(Boolean)
              .join(" ")
              .trim()
          : "";

      return (
        parts(farm?.user) ||
        farm?.ownerName ||
        parts(farm?.technician) ||
        parts(farm?.adminTechnician) ||
        ""
      );
    },

    // Registered By resolves technician -> adminTechnician -> owning user,
    // same precedence as the Farm Management list.
    getRegisteredBy(farm) {
      const registrant =
        farm?.technician || farm?.adminTechnician || farm?.user || null;

      if (!registrant) {
        return {
          registeredBy: "",
          registeredById: null,
          registeredByRole: "",
        };
      }

      const name =
        [registrant.firstName, registrant.middleName, registrant.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        registrant.fullName ||
        registrant.email ||
        registrant.mobile ||
        "";

      return {
        registeredBy: name,
        registeredById: registrant.id || null,
        registeredByRole:
          registrant.user_role?.[0]?.name || farm?.user?.user_role?.[0]?.name || "",
      };
    },

async fetchEudrStatusForFarms(farms) {
      if (!farms || farms.length === 0) return;

      try {
        // 1. Extract all IDs into a single array
        const farmIds = farms.map(farm => farm.id);

        // 2. Make the single batch API call
        const response = await DeforestationService.getProductionPlacesByCfFarmIds(farmIds);
        
        // Ensure we safely extract the array
        const productionPlaces = response?.data?.productionPlacesByCfFarmIds || [];

        // 3. Create a lookup dictionary for instant O(1) matching
        const productionPlaceMap = {};
        for (const pp of productionPlaces) {
          // Because we queried the DB by cf_farmid, we need to match the result back to the UI's farm.id.
          // We check the most likely fields your GraphQL schema uses to return that original ID.
          const matchingId = pp.farm?.cf_farmid || pp.farm?.id || pp.farmId;
          
          if (matchingId) {
            productionPlaceMap[matchingId] = pp;
          }
        }

        // 4. Apply the data back to our UI farms
        for (const farm of farms) {
          const productionPlaceData = productionPlaceMap[farm.id];

          if (productionPlaceData) {
            farm.productionPlaceData = productionPlaceData;
            farm.eudrStatus = this.determineEudrStatusFromProductionPlace(productionPlaceData);
            farm.riskAssessmentStatus = this.determineRiskAssessmentStatusFromProductionPlace(productionPlaceData);
            farm.status = this.determineOverallStatus(farm.eudrStatus, farm.riskAssessmentStatus);
          } else {
            // Default if this specific farm wasn't found in the batch response
            farm.eudrStatus = 'n/a';
            farm.riskAssessmentStatus = 'n/a';
            farm.status = 'n/a';
          }
        }
      } catch (error) {
        console.error('Failed to fetch batch EUDR statuses:', error);
        
        // Fallback for all farms if the single batch request fails
        for (const farm of farms) {
          farm.eudrStatus = 'n/a';
          farm.riskAssessmentStatus = 'n/a';
          farm.status = 'n/a';
        }
      }
    },

    determineEudrStatusFromProductionPlace(productionPlaceData) {
      if (!productionPlaceData) return 'n/a';
      
      if (productionPlaceData.productionPlaceDeforestationInfo?.deforestationReport?.overallProb) {
        return productionPlaceData.productionPlaceDeforestationInfo.deforestationReport.overallProb;
      }
      
      if (productionPlaceData.diligenceReportProductionPlaceArray && productionPlaceData.diligenceReportProductionPlaceArray.length > 0) {
        const firstPlace = productionPlaceData.diligenceReportProductionPlaceArray[0];
        if (firstPlace.productionPlaceDeforestationInfo?.deforestationReport?.overallProb) {
          return firstPlace.productionPlaceDeforestationInfo.deforestationReport.overallProb;
        }
      }
      
      return 'n/a';
    },

    determineRiskAssessmentStatusFromProductionPlace(productionPlaceData) {
      if (!productionPlaceData) return 'n/a';
      
      if (productionPlaceData.risk_assessment_status) {
        return productionPlaceData.risk_assessment_status;
      }
      
      if (productionPlaceData.diligenceReportProductionPlaceArray && productionPlaceData.diligenceReportProductionPlaceArray.length > 0) {
        const firstPlace = productionPlaceData.diligenceReportProductionPlaceArray[0];
        if (firstPlace.all_risk_assessments && firstPlace.all_risk_assessments.length > 0) {
          return firstPlace.all_risk_assessments[0].riskAssessmentStatus;
        }
      }
      
      if (productionPlaceData.all_risk_assessments && productionPlaceData.all_risk_assessments.length > 0) {
        return productionPlaceData.all_risk_assessments[0].riskAssessmentStatus;
      }
      
      return 'n/a';
    },

    determineEudrStatus(deforestationData) {
      if (!deforestationData || !deforestationData.overallProb) return 'n/a';
      return deforestationData.overallProb;
    },

    determineRiskAssessmentStatus(deforestationData) {
      if (!deforestationData) return 'n/a';
      
      const hasRiskData = deforestationData.metrics && deforestationData.metrics.length > 0;
      
      if (hasRiskData) {
        const highRiskMetrics = deforestationData.metrics.filter(metric => 
          metric.label.toLowerCase().includes('high') || 
          metric.percent > 0.5
        );
        
        if (highRiskMetrics.length === 0) {
          return 'approved';
        } else {
          return 'n/a';
        }
      }
      
      return 'n/a';
    },

    determineOverallStatus(eudrStatus, riskAssessmentStatus) {
      if (eudrStatus === 'zero-negligible' && riskAssessmentStatus === 'approved') {
        return 'compliant';
      } else if (eudrStatus === 'non-compliant' || riskAssessmentStatus === 'rejected') {
        return 'non-compliant';
      } else {
        return 'n/a';
      }
    },

    getFarmStatus(farm) {
      return farm.status || 'n/a';
    },

    selectItem(item) {
      this.selectedItem = item;
    },

    reportPageChange(t) {
      if (t) this.options.page = this.options.page + 1;
      else this.options.page = this.options.page - 1;
      this.getAssociatedFarms();
    },

    viewEudrReport(item) {
      console.log('Viewing EUDR report for farm:', item.id);
    },

    viewRiskAssessmentReport(item) {
      console.log('Viewing Risk Assessment report for farm:', item.id);
    },

    getEudrStatusText(status) {
      if (!status || status === 'n/a') return 'N/A';
      
      if (status.includes('Zero') || status.includes('Negligible')) {
        return this.$t("memberDataSection.zeroNegligibleProbability");
      } else if (status.includes('Low') && !status.includes('Very')) {
        return this.$t("memberDataSection.compliant");
      } else if (status.includes('Medium')) {
        return 'Medium Risk';
      } else if (status.includes('High') || status.includes('Very High')) {
        return this.$t("memberDataSection.nonCompliant");
      }
      return status;
    },

    getEudrStatusButtonClass(status) {
      if (!status || status === 'n/a') return 'gray-button';
      
      if (status.includes('Zero') || status.includes('Negligible')) {
        return 'green-button';
      } else if (status.includes('Low') && !status.includes('Very')) {
        return 'green-button';
      } else if (status.includes('Medium')) {
        return 'orange-button';
      } else if (status.includes('High') || status.includes('Very High')) {
        return 'red-button';
      }
      return 'gray-button';
    },

    getRiskAssessmentText(status) {
      const statusMap = {
        'approved': this.$t("memberDataSection.approved"),
        'rejected': this.$t("memberDataSection.rejected"),
        'mitigation_required': this.$t("memberDataSection.mitigationRequired"),
        'n/a': 'N/A'
      };
      return statusMap[status] || 'N/A';
    },

    getRiskAssessmentButtonClass(status) {
      const classMap = {
        'approved': 'green-button',
        'rejected': 'red-button',
        'mitigation_required': 'orange-button',
        'n/a': 'gray-button'
      };
      return classMap[status] || 'gray-button';
    },

    formatProbability(probability) {
      if (probability === null || probability === undefined) return 'N/A';
      return Math.round(probability * 100);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('DD-MMM-YYYY');
    },
  },
  components: {
    ConfirmBox,
  },
  mixins: [loadingMixin, download, getPermittedActions, DateMixin, RoleMixin],
};
</script>

<style lang="scss" scoped>
.blue-button {
  background-color: #d0e6fe !important;
  border: 2px solid #002287 !important;
  color: #002287 !important;
}

.orange-button {
  background-color: #fff7ec !important;
  border: 2px solid #ffa826 !important;
  color: #ffa826 !important;
}

::v-deep .wdt.v-text-field {
  width: 250px;
}

::v-deep .v-tab:hover {
  background-color: #caf1e2;
  color: $primary_color !important;
}

::v-deep .v-data-table-header span {
  text-transform: uppercase !important;
  color: black;
}

::v-deep .center-content {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .img-center {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 40%;
}

.gray-button {
  background-color: #e8e8e8 !important;
  border: 2px solid #000000 !important;
  color: #000000 !important;
}

.green-button {
  background-color: #d4edda !important;
  border: 2px solid #28a745 !important;
  color: #28a745 !important;
}

.red-button {
  background-color: #f8d7da !important;
  border: 2px solid #dc3545 !important;
  color: #dc3545 !important;
}

/* Dynamic icon colors for rebranding */
.dynamic-icon-color {
  color: var(--primary-color, #00BD73) !important;
}

/* Indonesian client specific icon color */
[data-theme="indonesian"] .dynamic-icon-color {
  color: #184980 !important;
}

/* Kenya client specific icon color */
[data-theme="kenya"] .dynamic-icon-color {
  color: #A75300 !important;
}

/* Default client icon color */
.dynamic-icon-color {
  color: var(--primary-color, #00BD73) !important;
}

/* Minimize gaps in action and expand columns */
::v-deep .action-cell,
::v-deep .expand-cell {
  padding: 8px 4px !important;
  text-align: center;
}

::v-deep .action-cell .v-btn,
::v-deep .expand-cell .v-btn {
  margin: 0 !important;
  min-width: 32px !important;
  width: 32px !important;
  height: 32px !important;
}

/* Standardize all status buttons */
::v-deep .green-button,
::v-deep .red-button,
::v-deep .gray-button {
  height: 32px !important;
}

/* Reduce padding in status column */
::v-deep td[data-value="status"] {
  padding-right: 8px !important;
}

/* Expanded content styling */
.expanded-content {
  background-color: #f8f9fa;
  padding: 0;
}

.horizontal-line {
  width: 100%;
  height: 1px;
  background-color: #dee2e6;
  margin: 0;
}

.two-column-layout {
  display: flex;
  min-height: 120px;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.column-left {
  flex: 0 0 auto;
  padding: 20px 30px 20px 20px;
  display: flex;
  flex-direction: column;
  min-width: 260px;
  max-width: 320px;
}

.column-right {
  flex: 0 0 auto;
  padding: 20px 20px 20px 30px;
  display: flex;
  flex-direction: column;
  min-width: 260px;
  max-width: 320px;
}

/* Responsive design for mobile and tablet */
@media (max-width: 768px) {
  .two-column-layout {
    flex-direction: column;
  }
  
  .column-left {
    padding: 15px 20px 10px 15px;
    min-width: 100%;
    max-width: 100%;
  }
  
  .column-right {
    padding: 10px 20px 15px 15px;
    min-width: 100%;
    max-width: 100%;
  }
  
  .vertical-line {
    display: none;
  }
  
  .horizontal-line {
    display: block;
    width: 100%;
    height: 1px;
    background-color: #dee2e6;
    margin: 0;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .column-left {
    padding: 18px 25px 18px 18px;
    min-width: 220px;
    max-width: 280px;
  }
  
  .column-right {
    padding: 18px 18px 18px 25px;
    min-width: 220px;
    max-width: 280px;
  }
}

@media (min-width: 1025px) {
  .column-left {
    padding: 20px 30px 20px 20px;
    min-width: 260px;
    max-width: 320px;
  }
  
  .column-right {
    padding: 20px 20px 20px 30px;
    min-width: 260px;
    max-width: 320px;
  }
}

.vertical-line {
  width: 2px;
  background-color: #dee2e6;
  margin: 20px 0;
  flex: 0 0 2px;
}

.section-header {
  margin-bottom: 15px;
}

.section-title {
  color: #28a745;
  font-size: 16px;
  font-weight: bold;
  margin: 0;
  text-align: left;
}

.section-body {
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 1;
  text-align: left;
}

.assessment-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
  text-align: left;
}

.assessment-label {
  font-weight: normal;
  color: #666;
  font-size: 14px;
  margin-bottom: 5px;
}

.assessment-value {
  font-weight: bold;
  color: #000;
  font-size: 14px;
}

.button-with-icon {
  display: flex;
  align-items: center;
}

.assessment-details {
  margin-top: 10px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 12px;
}

.detail-label {
  font-weight: bold;
  color: #666;
}

.detail-value {
  color: #000;
  font-weight: normal;
}

.metric-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 3px;
  font-size: 11px;
}

.metric-label {
  font-weight: bold;
  color: #666;
}

.metric-value {
  color: #000;
  font-weight: normal;
}
</style>